import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export const SimulateDialog = ({ onCreated }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ phone: "+39 ", name: "", text: "" });
  const [file, setFile] = useState(null);
  const [echo, setEcho] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      let data;
      if (echo) {
        ({ data } = await api.post("/simulate/echo", form));
      } else if (file) {
        const fd = new FormData();
        fd.append("phone", form.phone); fd.append("name", form.name); fd.append("caption", form.text); fd.append("file", file);
        ({ data } = await api.post("/simulate/incoming-media", fd));
      } else {
        ({ data } = await api.post("/simulate/incoming", form));
      }
      qc.invalidateQueries({ queryKey: ["conversations"] });
      toast.success(echo ? "Messaggio dal telefono simulato" : "Messaggio in arrivo simulato");
      onCreated?.(data.conversation_id);
      setOpen(false);
      setFile(null);
      setForm({ phone: "+39 ", name: "", text: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="simulate-incoming-open-button" className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-slate-600 border border-dashed border-slate-300 rounded-lg py-2 hover:bg-slate-50 transition-colors">
          <FlaskConical size={14} /> Simula messaggio in arrivo
        </button>
      </DialogTrigger>
      <DialogContent data-testid="simulate-incoming-dialog">
        <DialogHeader>
          <DialogTitle>Simula messaggio cliente</DialogTitle>
          <DialogDescription>Utile per testare la casella prima di collegare il webhook Meta. Segue lo stesso flusso del webhook (incluso il messaggio di assenza).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Numero</Label><Input data-testid="simulate-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="simulate-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>{file ? "Didascalia (opzionale)" : "Messaggio"}</Label><Textarea data-testid="simulate-text-input" required={!file} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></div>
          {!echo && <div className="space-y-1.5"><Label>Allegato (opzionale: foto, PDF, audio)</Label><Input data-testid="simulate-file-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf,audio/*" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>}
          <label className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-2.5 cursor-pointer">
            <input data-testid="simulate-echo-checkbox" type="checkbox" checked={echo} onChange={(e) => { setEcho(e.target.checked); setFile(null); }} className="mt-0.5 accent-[#25D366]" />
            <span><b>Inviato dal telefono (Coexistence)</b>: simula un messaggio scritto da voi nell'app WhatsApp Business del cellulare verso questo cliente.</span>
          </label>
          <Button type="submit" data-testid="simulate-submit-button" className="w-full bg-[#0F172A] hover:bg-[#1E293B]">Invia messaggio di test</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
